import { useState } from 'react';
import { IconButton, Icon, Tooltip, Popover, icon } from '@nula/core-ui';
import type { FormattingState } from '@/features/canvas/engine/hooks/use-formatting-state';
import styles from './align-group.module.css';

const ALIGN_ICONS: Record<'left' | 'center' | 'right', string> = {
  left: 'format_align_left',
  center: 'format_align_center',
  right: 'format_align_right',
};

const VALIGN_ICONS: Record<'top' | 'middle' | 'bottom', string> = {
  top: 'vertical_align_top',
  middle: 'vertical_align_center',
  bottom: 'vertical_align_bottom',
};

/**
 * Reusable text-align + vertical-align cluster, hosted inside a popover
 * triggered by an icon reflecting the current horizontal alignment.
 * Used by every node type that has formatting controls.
 */
export function AlignGroup({ state }: { state: FormattingState }) {
  const [open, setOpen] = useState(false);

  const content = (
    <div className={styles.alignPopover}>
      <div className={styles.styleGroup}>
        {(['left', 'center', 'right'] as const).map((align) => (
          <Tooltip key={align} title={`Align ${align}`} placement="top">
            <IconButton
              transparent
              aria-label={`Align ${align}`}
              className={styles.btn}
              onClick={() => { state.setTextAlign(align); setOpen(false); }}
            >
              <Icon name={ALIGN_ICONS[align]} size={icon.md} />
            </IconButton>
          </Tooltip>
        ))}
      </div>
      <div className={styles.styleGroup}>
        {(['top', 'middle', 'bottom'] as const).map((valign) => (
          <Tooltip key={valign} title={`Align ${valign}`} placement="top">
            <IconButton
              transparent
              aria-label={`Align ${valign}`}
              className={styles.btn}
              onClick={() => { state.setVerticalAlign(valign); setOpen(false); }}
            >
              <Icon name={VALIGN_ICONS[valign]} size={icon.md} />
            </IconButton>
          </Tooltip>
        ))}
      </div>
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title="Text alignment" placement="top" open={open ? false : undefined}>
        <IconButton
          transparent
          aria-label="Text alignment"
          aria-expanded={open}
          className={styles.btn}
        >
          <Icon name={ALIGN_ICONS[state.textAlign]} size={icon.md} />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}
